import React, { useEffect, useState } from 'react'

import { goodfellasApi } from '../../../apis'
import { MatchMaster } from '../../../types/competition'

interface MasterSuggestion {
  name: string
  profileUserUuid: string
  belt?: string
  gymUuid: string
  gymName?: string
}

interface MasterSelectProps {
  label: string
  /** Perfil do competidor a que este mestre pertence. */
  competitorUuid?: string | null
  value?: MatchMaster | null
  onChange: (master: MatchMaster | null) => void
}

/**
 * Mestre de um competidor, escolhido entre os instrutores das academias dele.
 *
 * É um `<select>`, não uma busca: a lista é curta e fechada. Sem competidor
 * vinculado — ou sem instrutor na academia dele — não há o que escolher, e o
 * campo fica desabilitado com o aviso do porquê, em vez de aceitar um nome
 * digitado que não corresponde a perfil nenhum.
 */
export default function MasterSelect({
  label,
  competitorUuid,
  value,
  onChange,
}: MasterSelectProps) {
  const [masters, setMasters] = useState<MasterSuggestion[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!competitorUuid) {
      setMasters([])
      return
    }

    let active = true
    const load = async () => {
      setLoading(true)
      try {
        const response = await goodfellasApi.get(
          `/competition/masters/${encodeURIComponent(competitorUuid)}`,
        )
        if (!active) return
        setMasters(
          response.data?.success && Array.isArray(response.data.data)
            ? response.data.data
            : [],
        )
      } catch (err) {
        console.error('Error loading masters:', err)
        if (active) setMasters([])
      } finally {
        if (active) setLoading(false)
      }
    }

    load()
    // Uma resposta atrasada de um competidor que já foi trocado encheria o
    // select com os instrutores da academia errada.
    return () => {
      active = false
    }
  }, [competitorUuid])

  // O mestre já gravado pode ter saído da academia desde então: some da lista,
  // mas continua sendo a resposta certa para esta luta. Sem esta opção extra o
  // select cairia em "Sem mestre" e o próximo salvar apagaria o dado.
  const options = [...masters]
  const storedUuid = value?.identificator?.uuid
  if (storedUuid && !options.some((m) => m.profileUserUuid === storedUuid)) {
    options.unshift({
      name: value?.name ?? '',
      profileUserUuid: storedUuid,
      gymUuid: '',
    })
  }

  const disabled = loading || options.length === 0
  const hint = !competitorUuid
    ? 'Vincule o perfil do competidor para escolher o mestre'
    : loading
    ? 'Carregando instrutores…'
    : options.length === 0
    ? 'Nenhum instrutor cadastrado na academia deste competidor'
    : null

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
      </label>
      <select
        value={value?.identificator?.uuid ?? ''}
        disabled={disabled}
        onChange={(e) => {
          const picked = options.find(
            (m) => m.profileUserUuid === e.target.value,
          )
          onChange(
            picked
              ? {
                  name: picked.name,
                  identificator: {
                    type: 'profile_user',
                    uuid: picked.profileUserUuid,
                  },
                }
              : null,
          )
        }}
        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
      >
        <option value="">Sem mestre</option>
        {options.map((master) => (
          <option key={master.profileUserUuid} value={master.profileUserUuid}>
            {master.gymName
              ? `${master.name} — ${master.gymName}`
              : master.name}
          </option>
        ))}
      </select>
      {hint && <p className="mt-2 text-sm text-gray-500">{hint}</p>}
    </div>
  )
}
